import { useEffect, useMemo, useRef, useState } from "react";
import { AiTranscriptLiveEvent } from "@/model/interface/AiTranscriptLiveEvent";
import { TranscriptLine } from "@/model/interface/AiAgent";
import { useLazyGetLiveAiCallTranscriptQuery } from "@/store/rest_api";
import { useSocketSubscription } from "@/hooks/useSocketSubscription";
import { useSocketResync } from "@/hooks/useSocketResync";

const lineKey = (line: TranscriptLine) => `${line.role}|${line.timestamp}`;

export function useAiTranscriptSocket(callId: string | null, callSid: string | null) {
  const [persistedLines, setPersistedLines] = useState<TranscriptLine[]>([]);
  const [liveLines, setLiveLines] = useState<TranscriptLine[]>([]);
  const [fetchTranscript] = useLazyGetLiveAiCallTranscriptQuery();
  const callIdRef = useRef(callId);

  const loadPersistedLines = async (loadingCallId: string) => {
    const entries = await fetchTranscript(loadingCallId)
      .unwrap()
      .catch(() => null);
    if (entries && callIdRef.current === loadingCallId) {
      setPersistedLines(entries);
    }
  };

  useEffect(() => {
    callIdRef.current = callId;
    setPersistedLines([]);
    setLiveLines([]);
    if (callId) {
      void loadPersistedLines(callId);
    }
  }, [callId, callSid]);

  useSocketSubscription(callSid ? `/topic/ai-transcripts/${callSid}` : null, (message) => {
    try {
      const event = JSON.parse(message.body) as AiTranscriptLiveEvent;
      setLiveLines((previous) => [...previous, { role: event.role, text: event.text, timestamp: event.timestamp }]);
    } catch (parseError) {
      console.error("Failed to parse AI transcript event", parseError);
    }
  });

  useSocketResync(() => {
    if (callId) {
      void loadPersistedLines(callId);
    }
  });

  const lines = useMemo(() => {
    const persistedKeys = new Set(persistedLines.map(lineKey));
    return [...persistedLines, ...liveLines.filter((line) => !persistedKeys.has(lineKey(line)))];
  }, [persistedLines, liveLines]);

  return { lines };
}
